import type { Href } from "expo-router";
import { useRouter } from "expo-router";
import { type ReactNode } from "react";
import { Linking, Platform, Pressable, Text, useWindowDimensions, View } from "react-native";
import Svg, { Circle, Line } from "react-native-svg";

import { COLORS, FONTS } from "../../lib/brand";
import { LogoMark, LogoWordmark } from "../brand/Logo";
import { PANE_THEMES } from "../brand/paneThemes";

// Wired to the /brand General pane — the single source of truth for marketing.
const THEME = PANE_THEMES.General;
const FOREST_DEEP = THEME.pageBg;
const AMBER = THEME.accent;
const LINEN = THEME.textOnSurface;
const SAGE = COLORS.SAGE.hex;

function pxPad(width: number): number {
  return width >= 900 ? 60 : 26;
}

export function MarketingFooter({ surface }: { surface?: string } = {}) {
  const { width: winW } = useWindowDimensions();
  const router = useRouter();
  const pad = pxPad(winW);
  const isWide = winW >= 860;

  const openStatus = () => {
    const url = "https://coravu.betteruptime.com";
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      void Linking.openURL(url);
    }
  };

  return (
    <View
      style={{
        backgroundColor: surface ?? FOREST_DEEP,
        borderTopWidth: 1,
        borderTopColor: "rgba(237,232,223,0.06)",
        paddingHorizontal: pad,
      }}
    >
      <View
        style={{
          flexDirection: isWide ? "row" : "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          gap: isWide ? 72 : 36,
          paddingTop: isWide ? 28 : 20,
          paddingBottom: isWide ? 16 : 12,
        }}
      >
        {/* Brand column — wheel + wordmark on the top baseline, tagline below. */}
        <View
          style={{
            flex: isWide ? 1.4 : undefined,
            minWidth: 240,
            maxWidth: isWide ? 360 : undefined,
            width: isWide ? undefined : "100%",
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
            <LogoMark size={26} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} />
            <LogoWordmark fontSize={20.4} letterSpacing={7} color={LINEN} accent={AMBER} />
          </View>
          <Text
            style={{
              marginTop: 8,
              fontFamily: FONTS.body,
              fontSize: 10.5,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: "rgba(247,244,238,0.35)",
              maxWidth: 280,
              lineHeight: 17.8,
            }}
          >
            Precision Talent · Case Recruitment · Est. 2026
          </Text>
        </View>

        <FooterColumn title="Resources" isWide={isWide}>
          <FooterLink label="FAQ" onPress={() => router.push("/faq" as Href)} />
          <FooterLink
            label="Career Opportunities"
            onPress={() => router.push("/student/find" as Href)}
          />
        </FooterColumn>

        <FooterColumn title="Company" isWide={isWide}>
          <FooterLink label="Contact" onPress={() => router.push("/contact" as Href)} />
          <FooterLink label="Website Health" onPress={openStatus} />
        </FooterColumn>

        <FooterColumn title="Legal" isWide={isWide}>
          <FooterLink label="Privacy Policy" onPress={() => router.push("/privacy" as Href)} />
          <FooterLink label="Terms of Service" onPress={() => router.push("/terms" as Href)} />
        </FooterColumn>
      </View>

      {/* Hairline divider — AMBER at 12%, full content width. */}
      <View
        style={{
          height: 1,
          backgroundColor: "rgba(181,135,58,0.12)",
          marginTop: isWide ? 14 : 10,
        }}
      />

      <View
        style={{
          paddingVertical: isWide ? 10 : 8,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 11.5,
            letterSpacing: 0.3,
            color: "rgba(247,244,238,0.4)",
            textAlign: "center",
          }}
        >
          © 2026 Coravu LLC · Built in Gainesville, FL
        </Text>
      </View>
    </View>
  );
}

function FooterColumn({
  title,
  children,
  isWide,
}: {
  title: string;
  children: ReactNode;
  isWide: boolean;
}) {
  return (
    <View
      style={{
        flex: isWide ? 1 : undefined,
        minWidth: 140,
        width: isWide ? undefined : "100%",
      }}
    >
      <Text
        style={{
          // Small top offset baselines the eyebrow against the wordmark's
          // visual top (the wheel SVG is the tallest element in the brand row,
          // so the link columns need a nudge to top-align cleanly).
          marginTop: 4,
          marginBottom: 10,
          fontFamily: FONTS.bodySemiBold,
          fontSize: 10.5,
          letterSpacing: 1.5,
          textTransform: "uppercase",
          color: AMBER,
        }}
      >
        {title}
      </Text>
      {children}
    </View>
  );
}

function FooterLink({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} accessibilityRole="link">
      {({ hovered }) => (
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 13.5,
            lineHeight: 20.3,
            marginBottom: 6,
            color: hovered && Platform.OS === "web" ? AMBER : "rgba(247,244,238,0.78)",
          }}
        >
          {label}
        </Text>
      )}
    </Pressable>
  );
}
